'use client';

import {useState, useEffect, useRef} from 'react';
import {useSearchParams} from 'next/navigation';
import dynamic from 'next/dynamic';
import MapSidebar from '@/components/map/MapSidebar';
import DelegationPanel from '@/components/map/DelegationPanel';
import type {Projet, DelegationInfo} from '@/components/map/TunisiaMap';

const TunisiaMap = dynamic(() => import('@/components/map/TunisiaMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-surface animate-pulse">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-primary/20 mx-auto mb-3 animate-pulse" />
        <p className="text-sm text-muted">Chargement de la carte...</p>
      </div>
    </div>
  ),
});

const categories = [
  {value: '', label: 'Toutes categories'},
  {value: 'sante', label: 'Sante'},
  {value: 'education', label: 'Education'},
  {value: 'environnement', label: 'Environnement'},
  {value: 'social', label: 'Social'},
  {value: 'infrastructure', label: 'Infrastructure'},
  {value: 'culture', label: 'Culture'},
  {value: 'logement', label: 'Logement'},
  {value: 'microcredit', label: 'Microcredit'},
];

/* Status color for accomplished projects — all "termine" (gray-green tones) */
const ACCOMPLI_COLOR = '#2E7D32';

interface AccompliStats {
  total: number;
  totalBudget: number;
  totalCollecte: number;
  totalBeneficiaires: number;
  byCategory: Record<string, number>;
  byGouvernorat: Record<string, number>;
}

function computeStats(projets: Projet[]): AccompliStats {
  const stats: AccompliStats = {
    total: projets.length,
    totalBudget: 0,
    totalCollecte: 0,
    totalBeneficiaires: 0,
    byCategory: {},
    byGouvernorat: {},
  };
  for (const p of projets) {
    stats.totalBudget += p.budget_objectif || 0;
    stats.totalCollecte += p.montant_collecte || 0;
    stats.totalBeneficiaires += (p as unknown as Record<string, number>).nb_beneficiaires || 0;
    stats.byCategory[p.categorie] = (stats.byCategory[p.categorie] || 0) + 1;
    stats.byGouvernorat[p.gouvernorat] = (stats.byGouvernorat[p.gouvernorat] || 0) + 1;
  }
  return stats;
}

export default function ProjetsAccomplisPage() {
  const searchParams = useSearchParams();
  const projetIdParam = searchParams.get('projet');
  const [filters, setFilters] = useState({categorie: '', statut: 'termine', urgentOnly: false});
  const [selectedProjet, setSelectedProjet] = useState<Projet | null>(null);
  const [selectedDeleg, setSelectedDeleg] = useState<DelegationInfo | null>(null);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [showStats, setShowStats] = useState(false);
  const autoSelectedRef = useRef(false);

  const panelOpen = !!(selectedProjet || selectedDeleg);
  const stats = computeStats(projets);

  /* Hide global breadcrumb + footer on this fullscreen map page */
  useEffect(() => {
    const breadcrumb = document.querySelector('nav[aria-label="Breadcrumb"]') as HTMLElement;
    const footer = document.querySelector('footer') as HTMLElement;
    if (breadcrumb) breadcrumb.style.display = 'none';
    if (footer) footer.style.display = 'none';
    return () => {
      if (breadcrumb) breadcrumb.style.display = '';
      if (footer) footer.style.display = '';
    };
  }, []);

  /* Fetch accomplished projects from backend */
  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
    fetch(`${apiUrl}/api/v1/carte/projets-accomplis`)
      .then(r => r.ok ? r.json() : null)
      .then(geojson => {
        if (!geojson?.features) return;
        setProjets(geojson.features.map((f: { properties: Record<string, unknown>; geometry: { coordinates: number[] } }) => {
          const p = f.properties;
          return {
            id: p.id as number,
            titre: (p.titre || p.titre_fr || '') as string,
            categorie: (p.categorie || '') as string,
            statut: 'termine',
            budget_objectif: (p.budget_objectif || 0) as number,
            montant_collecte: (p.montant_collecte || 0) as number,
            gouvernorat: (p.gouvernorat || '') as string,
            delegation: (p.delegation || '') as string,
            imada: (p.imada || '') as string,
            jalons: [],
            photos: [],
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
            nb_beneficiaires: (p.nb_beneficiaires || 0) as number,
            date_cloture: (p.date_cloture || '') as string,
            porteur_nom: (p.porteur_nom || '') as string,
          };
        }));
      })
      .catch(() => {});
  }, []);

  /* Auto-select projet from ?projet=ID */
  useEffect(() => {
    if (!projetIdParam || projets.length === 0 || autoSelectedRef.current) return;
    const target = projets.find(p => String(p.id) === projetIdParam);
    if (target) {
      setSelectedProjet(target);
      autoSelectedRef.current = true;
    }
  }, [projetIdParam, projets]);

  const filteredProjets = projets.filter(p => {
    if (filters.categorie && p.categorie !== filters.categorie) return false;
    return true;
  });

  return (
    <div className="flex flex-col" style={{height: 'calc(100vh - 110px)'}}>
      {/* Map + Side panel */}
      <div className="flex flex-1 min-h-0 relative">
        {/* Map container */}
        <div className={`relative transition-all duration-300 ${panelOpen ? 'flex-1' : 'w-full'}`}>
          <TunisiaMap
            filters={{...filters, statut: 'termine'}}
            focusPoint={selectedProjet && autoSelectedRef.current ? {lng: selectedProjet.lng, lat: selectedProjet.lat} : undefined}
            onSelectProjet={(p) => { setSelectedProjet(p); setSelectedDeleg(null); }}
            onSelectDelegation={(d) => { setSelectedDeleg(d); setSelectedProjet(null); }}
            projets={filteredProjets}
          />

          {/* Overlay: Title banner */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
            {/* Title badge */}
            <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-lg px-4 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-sm font-bold text-primary leading-tight">Projets Accomplis</h1>
                <p className="text-[10px] text-muted">{stats.total} projets termines avec succes</p>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
              <select
                value={filters.categorie}
                onChange={e => setFilters({...filters, categorie: e.target.value})}
                className="px-2 py-1 text-xs border border-gray-200 rounded bg-white text-gray-700 focus:ring-1 focus:ring-[#1B5E20] outline-none"
              >
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <button
                onClick={() => setShowStats(!showStats)}
                className={`px-2 py-1 text-xs rounded transition-colors ${showStats ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Statistiques
              </button>
              <span className="text-[10px] text-gray-400 ml-1">{filteredProjets.length} projets</span>
            </div>

            {/* Stats overlay */}
            {showStats && (
              <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-lg px-4 py-3 max-w-xs">
                <h3 className="text-xs font-bold text-primary mb-2">Bilan des projets accomplis</h3>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="bg-primary/5 rounded p-2">
                    <div className="font-bold text-primary text-sm">{stats.total}</div>
                    <div className="text-muted">Projets termines</div>
                  </div>
                  <div className="bg-primary/5 rounded p-2">
                    <div className="font-bold text-primary text-sm">{stats.totalBeneficiaires.toLocaleString('fr-TN')}</div>
                    <div className="text-muted">Bénéficiaires</div>
                  </div>
                  <div className="bg-primary/5 rounded p-2">
                    <div className="font-bold text-primary text-sm">{(stats.totalCollecte / 1000).toFixed(0)}K</div>
                    <div className="text-muted">TND collectes</div>
                  </div>
                  <div className="bg-primary/5 rounded p-2">
                    <div className="font-bold text-primary text-sm">{Object.keys(stats.byGouvernorat).length}</div>
                    <div className="text-muted">Gouvernorats</div>
                  </div>
                </div>
                {Object.keys(stats.byCategory).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-[10px] font-semibold text-muted mb-1">Par catégorie :</div>
                    {Object.entries(stats.byCategory).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                      <div key={cat} className="flex items-center justify-between text-[10px]">
                        <span className="text-gray-600 capitalize">{cat}</span>
                        <span className="font-medium text-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Side panel — 360px fixed when open */}
        {panelOpen && (
          <div className="w-[360px] max-w-[35%] border-s border-border bg-white overflow-hidden flex flex-col shrink-0">
            {selectedDeleg && (
              <DelegationPanel
                delegation={selectedDeleg}
                projets={filteredProjets}
                onClose={() => setSelectedDeleg(null)}
                onSelectProjet={(p) => { setSelectedProjet(p); setSelectedDeleg(null); }}
              />
            )}
            {selectedProjet && !selectedDeleg && (
              <MapSidebar
                projet={selectedProjet}
                onClose={() => setSelectedProjet(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* Compact footer */}
      <div className="shrink-0 bg-[#0D3B12] text-white/70 px-4 py-2 flex items-center justify-between text-xs border-t border-white/10">
        <div className="flex items-center gap-3">
          <img src="/images/utss/rose.svg" alt="UTSS" className="h-5 w-auto opacity-60" />
          <span>&copy; 2026 UTSS — Projets Accomplis</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="tel:+21671808066" className="hover:text-white transition-colors">(+216) 71 808 066</a>
          <a href="mailto:utss@planet.tn" className="hover:text-white transition-colors">utss@planet.tn</a>
        </div>
      </div>
    </div>
  );
}
